import { get, patch } from '@core/Services/ApiService'
import { BonusReward, CampaignV2, GetCampaignsResponse } from './types'

const enum LocalConstants {
  ApiPrefix = '/api/bonuses',
  ApiVersion = 'v1',
  BaseUrl = `${ApiPrefix}/${ApiVersion}`,
}

export async function getCampaigns(): Promise<GetCampaignsResponse | undefined> {
  const response = await get<BonusReward[]>({
    url: `${LocalConstants.BaseUrl}/bonuses`,
    opts: {
      headers: {},
    },
  }).then(response => {
    return response?.map(mapToCampaignV2)
  })

  return response
}

export async function patchSplitBonusBet(bonusBetId: number, numberOfTokens: number) {
  await patch({
    url: `${LocalConstants.BaseUrl}/bonus-tokens/${bonusBetId}`,
    body: { numberOfTokens: numberOfTokens },
  })
}

const mapToCampaignV2 = (bonusReward: BonusReward) => {
  const campaignV2: CampaignV2 = {
    id: bonusReward.campaignSequence,
    triggerType: 'AccountDefaultTrigger',
    rewardType: 'Reward',
    title: bonusReward.title,
    description: bonusReward.description,
    expiry: bonusReward.expiryDatetime,
    rewardExpiry: bonusReward.rewardExpiryDatetime,
    minimumAmount:
      (bonusReward.triggerTypeName === 'Deposit'
        ? bonusReward.triggerMinDepositAmount
        : bonusReward.triggerMinBetAmount) ?? 0,
    initialAmount: bonusReward.maxRedeemableAmount ?? 0,
    remainingAmount:
      (bonusReward.isBonusBetReward ? bonusReward.tokenAmount : bonusReward.availableAmount) ?? 0,
    isActivated: bonusReward.isOfferActivated,
    isActivationPending: bonusReward.isOfferPendingActivation,
    isExpired: bonusReward.isOfferExpired,
    isLive: bonusReward.isOfferLive,
    isNotActivated: bonusReward.isOfferNotActivated,
    isRedeemed: false,
    redeemableBy: determineRedeemableBy(bonusReward),
    redemptionDetails:
      !!bonusReward.triggerFixtureId && !!bonusReward.triggerFixtureDate
        ? {
            race: {
              fixtureId: bonusReward.triggerFixtureId,
              raceNumber: Number.parseInt(bonusReward.triggerRaceNumber ?? '0'),
              fixtureDate: bonusReward.triggerFixtureDate,
            },
          }
        : null,
    tokenCanBeSplit: bonusReward.tokenCanBeSplit,
    splitTokenOptions: bonusReward.splitTokenOptions,
  }

  switch (bonusReward.triggerTypeName) {
    case 'Bet':
      campaignV2.triggerType = 'AccountBetTrigger'
      break
    case 'Deposit':
      campaignV2.triggerType = 'AccountDepositTrigger'
      break
  }

  switch (bonusReward.rewardTypeName) {
    case 'Bonus Bet':
      campaignV2.rewardType = 'BonusBetReward'
      break
    case 'Bonus Cash':
      campaignV2.rewardType = 'BonusCashReward'
      break
    case 'Account Credit':
      campaignV2.rewardType = 'CashReward'
      break
  }

  return campaignV2
}

const determineRedeemableBy = (bonusReward: BonusReward) => {
  const redeemableByByGroupName = {
    'any product': ['FobRacingBet,', 'FobSportsBet,', 'ToteRacingBet,', 'ToteSportsBet,'],
    'fob mixed': ['FobRacingBet,', 'FobSportsBet,'],
    'fob racing': ['FobRacingBet,'],
    'fob sports': ['FobSportsBet,'],
    'tote racing': ['ToteRacingBet,'],
    'tote sports': ['ToteSportsBet,'],
  }

  let result = ''

  bonusReward.rewardProductGroups?.forEach(grp => {
    const name = grp.name.toLowerCase() as
      | 'any product'
      | 'fob mixed'
      | 'fob racing'
      | 'fob sports'
      | 'tote racing'
      | 'tote sports'
    const redeemableBy = redeemableByByGroupName[name]

    result = redeemableBy.reduce((res, rb) => (result.indexOf(rb) < 0 ? res + rb : res), result)
  })

  if (result.charAt(result.length - 1) === ',') {
    result = result.substring(0, result.length - 1)
  }

  return result
}
